import { useEffect, useState } from "react";
import { ArrowsClockwise, ChatCircleText, CheckCircle, Copy, Link as LinkIcon, PaperPlaneTilt, Plus, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { Badge, Card, act, fmtStamp, useLoad } from "./shared";
import EmployeeRow, { EMP_COLS } from "./EmployeeRow";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);
const t12 = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";
const copy = (s) => navigator.clipboard?.writeText(s).then(() => toast.success("Link copied"), () => window.prompt("Copy this link", s));
const TONE = { delivered: "green", sent: "green", queued: "blue", sending: "blue", accepted: "blue", skipped_already_submitted: "slate", failed: "red", undelivered: "red", would_send: "amber", not_enrolled: "slate" };

function Schedule() {
  const { data, reload } = useLoad("/timesheet/sms/settings");
  const [s, setS] = useState(null);
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return null;
  const save = async () => { if (await act(() => api.put("/timesheet/sms/settings", { enabled: s.enabled, days: s.days, time: s.time, followup_enabled: s.followup_enabled, followup_time: s.followup_time, template: s.template }), "Reminder schedule saved")) reload(); };
  const p = data.provider;
  return (
    <Card title="Daily reminder" testId="sms-schedule" subtitle="Eastern Time (handles daylight saving). Employees who already submitted for today are skipped."
      right={p.configured && !p.ready ? <Badge tone="amber" testId="sms-provider-status"><Warning size={12} className="mr-1" />Twilio connected · Blocked/Pending A2P — texts won't send yet</Badge> : p.configured ? <Badge tone="green" testId="sms-provider-status"><CheckCircle size={12} className="mr-1" />Twilio connected · from {p.from}</Badge> : <Badge tone="amber" testId="sms-provider-status"><Warning size={12} className="mr-1" />Twilio not connected — texts won't send yet</Badge>}>
      <div className="space-y-4">
        <label className="flex items-center gap-3 text-sm"><Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="sms-enabled" />Send automatic reminders</label>
        <div className="flex flex-wrap gap-1.5" data-testid="sms-days">
          {DAYS.map((d, i) => {
            const on = s.days.includes(i);
            return <button key={d} type="button" onClick={() => setS({ ...s, days: on ? s.days.filter((x) => x !== i) : [...s.days, i] })} className={`h-8 px-3 rounded-full text-xs font-semibold border ${on ? "bg-[#327de3] border-[#327de3] text-white" : "border-slate-200 text-slate-500"}`} data-testid={`sms-day-${i}`}>{d}</button>;
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-slate-600">Send at</span>
          <select className={sel} value={s.time} onChange={(e) => setS({ ...s, time: e.target.value })} data-testid="sms-time">{TIMES.map((t) => <option key={t} value={t}>{t12(t)}</option>)}</select>
          <label className="flex items-center gap-2 text-slate-600"><Switch checked={s.followup_enabled} onCheckedChange={(v) => setS({ ...s, followup_enabled: v })} data-testid="sms-followup-enabled" />Follow-up if still missing at</label>
          <select className={sel} value={s.followup_time} disabled={!s.followup_enabled} onChange={(e) => setS({ ...s, followup_time: e.target.value })} data-testid="sms-followup-time">{TIMES.map((t) => <option key={t} value={t}>{t12(t)}</option>)}</select>
        </div>
        <div className="space-y-1">
          <Textarea rows={2} value={s.template} onChange={(e) => setS({ ...s, template: e.target.value })} data-testid="sms-template" />
          <p className="text-[11px] text-slate-400">{"{link}"} is replaced with the employee's personal link. “Reply STOP to opt out.” is added automatically.</p>
        </div>
        <Button onClick={save} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" size="sm" data-testid="sms-save-btn">Save schedule</Button>
      </div>
    </Card>
  );
}

function Employees({ onSent }) {
  const { data, reload } = useLoad("/timesheet/employees");
  const [name, setName] = useState("");
  const add = async () => { if (name.trim() && (await act(() => api.post("/timesheet/employees", { name: name.trim() }), "Employee added"))) { setName(""); reload(); } };
  const send = async (e, override = false) => {
    const r = await act(() => api.post("/timesheet/sms/send", { employee_id: e.id, override }));
    if (!r) return;
    if (r.already_submitted && !override) {
      if (window.confirm(`${e.name} already submitted today's timesheet. Send the reminder anyway? (Logged as an override.)`)) send(e, true);
      return;
    }
    r.ok ? toast.success(`Reminder sent to ${e.name}${r.already_submitted ? " (override)" : ""}`) : toast.error(r.error || "Send failed");
    onSent();
  };
  return (
    <Card title="Employees & mobile numbers" testId="sms-employees"
      subtitle="The same names shown on the form. Hidden employees can't submit or get texts; their history stays."
      right={data?.general_link && <Button variant="outline" size="sm" onClick={() => copy(data.general_link)} data-testid="sms-copy-general-link"><LinkIcon size={14} className="mr-1" />Copy general form link</Button>}>
      <div className="mb-3 flex gap-2 max-w-md">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add employee (name as it appears on timesheets)" data-testid="emp-new-input" />
        <Button onClick={add} variant="outline" data-testid="emp-add-btn"><Plus size={14} className="mr-1" />Add</Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{EMP_COLS.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">{(data?.items || []).map((e) => <EmployeeRow key={`${e.id}-${e.phone}-${e.link}`} e={e} onChanged={reload} onSend={send} />)}</tbody>
        </table>
      </div>
    </Card>
  );
}

function Activity({ tick }) {
  const { data, reload } = useLoad("/timesheet/sms/log", { tick });
  const [prev, setPrev] = useState(null);
  const run = async () => { const r = await act(() => api.post("/timesheet/sms/preview")); if (r) setPrev(r); };
  return (
    <Card title="Send activity" testId="sms-activity" subtitle="Every send, skip and delivery update."
      right={<div className="flex gap-2"><Button variant="outline" size="sm" onClick={run} data-testid="sms-preview-btn"><ChatCircleText size={14} className="mr-1" />Who would get a text now?</Button><Button variant="ghost" size="sm" onClick={reload} data-testid="sms-log-refresh"><ArrowsClockwise size={14} /></Button></div>}>
      {prev && (
        <div className="mb-4 rounded-lg bg-slate-50 p-3" data-testid="sms-preview-result">
          <div className="text-xs font-semibold text-slate-600 mb-2">Check for {prev.work_date} (nothing was sent)</div>
          <div className="flex flex-wrap gap-1.5">{prev.items.map((r) => <Badge key={r.employee} tone={TONE[r.status]}>{r.employee}: {r.status.replace(/_/g, " ")}</Badge>)}</div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr>{["When", "Employee", "Work date", "Type", "Status", "Detail"].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.items || []).map((r) => (
              <tr key={r.id} data-testid={`sms-log-${r.id}`}>
                <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{fmtStamp(r.at)}</td>
                <td className="px-2 py-1.5">{r.employee}</td>
                <td className="px-2 py-1.5 text-xs">{r.work_date}</td>
                <td className="px-2 py-1.5 text-xs">{r.kind}{r.override ? " · override" : ""}</td>
                <td className="px-2 py-1.5"><Badge tone={TONE[r.status] || "slate"}>{r.status.replace(/_/g, " ")}</Badge></td>
                <td className="px-2 py-1.5 text-xs text-slate-500">{r.error || r.to || ""}</td>
              </tr>
            ))}
            {!data?.items?.length && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400 text-sm">No reminders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Inbound() {
  const { data, reload } = useLoad("/timesheet/sms/inbound");
  const { data: cfg } = useLoad("/timesheet/sms/config");
  if (!data?.configured) return null;
  const connect = async () => { const r = await act(() => api.post("/timesheet/sms/inbound/connect")); if (r?.ok) { toast.success("Replies now reach Strata"); reload(); } else if (r) toast.error(r.error); };
  return (
    <div className="space-y-2">
      {cfg && (
        <div className={`rounded-lg px-3 py-2 text-xs border ${cfg.ready ? "border-[#10b981]/30 bg-[#10b981]/10 text-[#10b981]" : "border-[#f59e0b]/30 bg-[#f59e0b]/10 text-[#d97706]"}`} data-testid="sms-config-status">
          <b>SMS Configuration Status:</b> {cfg.ready ? "Ready" : "Blocked/Pending — texts are logged as blocked, not sent"}
          <div className="mt-0.5 text-[11px] opacity-90">Account {cfg.account} · Sender {cfg.from || "—"}{cfg.number_on_account ? "" : " (NOT on this Twilio account)"} · Messaging Service {cfg.messaging_service || "none linked"} · A2P brand {cfg.brand_status} · campaign {cfg.campaign_status}{cfg.error ? ` · ${cfg.error}` : ""}</div>
        </div>
      )}
    <div className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-xs ${data.connected ? "bg-[#10b981]/10 text-[#10b981] border border-[#10b981]/30" : "bg-[#f59e0b]/10 text-[#d97706] border border-[#f59e0b]/30"}`} data-testid="sms-inbound-status">
      {data.connected ? <CheckCircle size={14} weight="bold" /> : <Warning size={14} weight="bold" />}
      {data.connected ? "YES / STOP replies are delivered to this Strata environment." : `Replies to your Twilio number go to ${data.current_url || "nowhere"} — YES/STOP won't be recorded here.`}
      {!data.connected && <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={connect} data-testid="sms-inbound-connect">Point replies here</Button>}
    </div>
    </div>
  );
}

export default function SmsSettingsTab() {
  const [tick, setTick] = useState(0);
  return (
    <div className="space-y-4">
      <Inbound />
      <Schedule />
      <Employees onSent={() => setTick((t) => t + 1)} />
      <Activity tick={tick} />
    </div>
  );
}
